import { useEffect, useRef, useState } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

let scriptPromise = null;
function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget. No-op if VITE_TURNSTILE_SITE_KEY is not set
 * (dev mode without captcha — backend also skips verification in that case).
 *
 * Calls `onToken(token|null)` — null on reset/expire/error so consumers
 * can gate submit buttons off that state.
 */
export default function Turnstile({ onToken, action = 'signup', theme = 'auto' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!SITE_KEY) return; // Captcha disabled → skip rendering
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          theme,
          callback: (token) => onToken?.(token),
          'expired-callback': () => onToken?.(null),
          'error-callback': () => { setError('Captcha falló. Recarga la página.'); onToken?.(null); },
          'timeout-callback': () => onToken?.(null),
        });
      })
      .catch((e) => setError(e.message));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile?.remove) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* already removed */ }
      }
    };
  }, []);

  if (!SITE_KEY) return null;

  return (
    <div style={{ marginTop: 10, marginBottom: 8 }}>
      <div ref={containerRef} />
      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

export const TURNSTILE_ENABLED = !!SITE_KEY;
